/**
 * Screen Engagement Tracking Hook
 * 
 * Automatically tracks:
 * - Time spent on screen
 * - Scroll depth
 * - Interactions count
 * - Sends data when user leaves the screen
 */

import { useEffect, useRef, useCallback } from 'react';
import { analytics } from './amplitude';

interface ScreenEngagementOptions {
  screenName: 'research' | 'explore' | 'saved' | 'profile' | 'article' | 'landing';
  researchId?: string; // For article pages
  isOwnProfile?: boolean; // For profile pages
}

interface EngagementData {
  startTime: number;
  lastFlushAt: number;
  interactionsCount: number;
  maxScrollDepth: number;
  scrollMilestones: Set<number>;
  tabsViewed: Set<string>;
  sourcesClicked: number;
  cardsViewed: number;
  cardsClicked: number;
  filterChanges: number;
  itemsUnsaved: number;
  modalOpened: boolean;
  podcastPlayed: boolean;
  shared: boolean;
  saved: boolean;
  fullReportExpanded: boolean;
  settingsOpened: boolean;
  timeToFirstScroll: number | null;
  categoriesBrowsed: Set<string>;
}

export function useScreenEngagement(options: ScreenEngagementOptions) {
  const { screenName, researchId, isOwnProfile } = options;
  
  const engagementRef = useRef<EngagementData>({
    startTime: Date.now(),
    lastFlushAt: 0,
    interactionsCount: 0,
    maxScrollDepth: 0,
    scrollMilestones: new Set(),
    tabsViewed: new Set(['overview']), // Default tab
    sourcesClicked: 0,
    cardsViewed: 0,
    cardsClicked: 0,
    filterChanges: 0,
    itemsUnsaved: 0,
    modalOpened: false,
    podcastPlayed: false,
    shared: false,
    saved: false,
    fullReportExpanded: false,
    settingsOpened: false,
    timeToFirstScroll: null,
    categoriesBrowsed: new Set(),
  });

  // Track scroll depth
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = scrollHeight > 0 ? Math.round((scrollTop / scrollHeight) * 100) : 0;
    
    const data = engagementRef.current;
    
    // Track time to first scroll
    if (data.timeToFirstScroll === null && scrollPercent > 0) {
      data.timeToFirstScroll = (Date.now() - data.startTime) / 1000;
    }
    
    // Update max scroll depth
    if (scrollPercent > data.maxScrollDepth) {
      data.maxScrollDepth = scrollPercent;
      
      // Track milestones (25%, 50%, 75%, 100%)
      const milestones = [25, 50, 75, 100];
      for (const milestone of milestones) {
        if (scrollPercent >= milestone && !data.scrollMilestones.has(milestone)) {
          data.scrollMilestones.add(milestone);
          const timeToReach = (Date.now() - data.startTime) / 1000;
          analytics.trackScrollDepth(screenName, milestone, timeToReach);
        }
      }
    }
  }, [screenName]);

  // Track any interaction
  const trackInteraction = useCallback((type: string = 'click', elementType: string = 'unknown', elementId?: string) => {
    engagementRef.current.interactionsCount++;
    analytics.trackContentInteraction(screenName, type, elementType, elementId);
  }, [screenName]);

  // Track card viewed (for explore/research pages)
  const trackCardViewed = useCallback(() => {
    engagementRef.current.cardsViewed++;
  }, []);

  // Track card clicked
  const trackCardClicked = useCallback(() => {
    engagementRef.current.cardsClicked++;
    engagementRef.current.interactionsCount++;
  }, []);

  // Track filter change
  const trackFilterChange = useCallback(() => {
    engagementRef.current.filterChanges++;
    engagementRef.current.interactionsCount++;
  }, []);

  // Track modal opened
  const trackModalOpened = useCallback(() => {
    engagementRef.current.modalOpened = true;
    engagementRef.current.interactionsCount++;
  }, []);

  // Track tab viewed (for article pages)
  const trackTabViewed = useCallback((tabName: string) => {
    engagementRef.current.tabsViewed.add(tabName);
    engagementRef.current.interactionsCount++;
  }, []);

  // Track source clicked
  const trackSourceClicked = useCallback(() => {
    engagementRef.current.sourcesClicked++;
    engagementRef.current.interactionsCount++;
  }, []);

  // Track podcast played
  const trackPodcastPlayed = useCallback(() => {
    engagementRef.current.podcastPlayed = true;
    engagementRef.current.interactionsCount++;
  }, []);

  // Track shared
  const trackShared = useCallback(() => {
    engagementRef.current.shared = true;
    engagementRef.current.interactionsCount++;
  }, []);

  // Track saved
  const trackSaved = useCallback(() => {
    engagementRef.current.saved = true;
    engagementRef.current.interactionsCount++;
  }, []);

  // Track unsaved
  const trackUnsaved = useCallback(() => {
    engagementRef.current.itemsUnsaved++;
    engagementRef.current.interactionsCount++;
  }, []);

  // Track full report expanded
  const trackFullReportExpanded = useCallback(() => {
    engagementRef.current.fullReportExpanded = true;
    engagementRef.current.interactionsCount++;
  }, []);

  // Track settings opened
  const trackSettingsOpened = useCallback(() => {
    engagementRef.current.settingsOpened = true;
    engagementRef.current.interactionsCount++;
  }, []);

  // Track category browsed
  const trackCategoryBrowsed = useCallback((category: string) => {
    engagementRef.current.categoriesBrowsed.add(category);
    engagementRef.current.interactionsCount++;
  }, []);

  // Send engagement data when leaving the screen
  const sendEngagementData = useCallback(() => {
    const data = engagementRef.current;
    const now = Date.now();
    if (data.lastFlushAt > 0 && now - data.lastFlushAt < 1000) {
      return;
    }

    const timeSpentSeconds = Math.round((Date.now() - data.startTime) / 1000);
    
    // Don't track if less than 1 second
    if (timeSpentSeconds < 1) return;
    
    // Don't track if user is not authenticated (prevents anonymous events)
    if (!analytics.isUserAuthenticated()) {
      console.log(`[Analytics] Skipping engagement data - user not authenticated`);
      return;
    }

    // Track general screen time
    data.lastFlushAt = now;
    analytics.trackScreenTime(
      screenName,
      timeSpentSeconds,
      data.interactionsCount,
      data.maxScrollDepth
    );

    // Track screen-specific engagement
    switch (screenName) {
      case 'research':
        analytics.trackResearchPageEngagement({
          time_spent_seconds: timeSpentSeconds,
          research_cards_viewed: data.cardsViewed,
          research_cards_clicked: data.cardsClicked,
          filter_changes: data.filterChanges,
          new_research_modal_opened: data.modalOpened,
          scroll_depth_percent: data.maxScrollDepth,
        });
        break;

      case 'explore':
        analytics.trackExplorePageEngagement({
          time_spent_seconds: timeSpentSeconds,
          cards_viewed: data.cardsViewed,
          cards_clicked: data.cardsClicked,
          categories_browsed: Array.from(data.categoriesBrowsed),
          scroll_depth_percent: data.maxScrollDepth,
        });
        analytics.trackExploreBrowsed({
          items_loaded: data.cardsViewed,
          items_clicked: data.cardsClicked,
          category_filter: Array.from(data.categoriesBrowsed).at(-1),
          scroll_depth_percent: data.maxScrollDepth,
          time_spent_seconds: timeSpentSeconds,
        });
        break;

      case 'saved':
        analytics.trackSavedPageEngagement({
          time_spent_seconds: timeSpentSeconds,
          saved_items_count: data.cardsViewed,
          items_clicked: data.cardsClicked,
          items_unsaved: data.itemsUnsaved,
          scroll_depth_percent: data.maxScrollDepth,
        });
        break;

      case 'profile':
        analytics.trackProfilePageEngagement({
          time_spent_seconds: timeSpentSeconds,
          is_own_profile: isOwnProfile ?? true,
          research_items_viewed: data.cardsViewed,
          research_items_clicked: data.cardsClicked,
          settings_opened: data.settingsOpened,
          scroll_depth_percent: data.maxScrollDepth,
        });
        break;

      case 'article':
        if (researchId) {
          analytics.trackArticlePageEngagement({
            research_id: researchId,
            time_spent_seconds: timeSpentSeconds,
            scroll_depth_percent: data.maxScrollDepth,
            tabs_viewed: Array.from(data.tabsViewed),
            sources_clicked: data.sourcesClicked,
            podcast_played: data.podcastPlayed,
            shared: data.shared,
            saved: data.saved,
            full_report_expanded: data.fullReportExpanded,
            time_to_first_scroll_seconds: data.timeToFirstScroll ?? undefined,
          });
        }
        break;
    }
  }, [screenName, researchId, isOwnProfile]);

  // Set up scroll listener and cleanup
  useEffect(() => {
    // Reset engagement data when screen changes
    engagementRef.current = {
      startTime: Date.now(),
      lastFlushAt: 0,
      interactionsCount: 0,
      maxScrollDepth: 0,
      scrollMilestones: new Set(),
      tabsViewed: new Set(['overview']),
      sourcesClicked: 0,
      cardsViewed: 0,
      cardsClicked: 0,
      filterChanges: 0,
      itemsUnsaved: 0,
      modalOpened: false,
      podcastPlayed: false,
      shared: false,
      saved: false,
      fullReportExpanded: false,
      settingsOpened: false,
      timeToFirstScroll: null,
      categoriesBrowsed: new Set(),
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Track page visibility changes (user switches tabs)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        sendEngagementData();
      } else {
        // Reset start time when user comes back
        engagementRef.current.startTime = Date.now();
        engagementRef.current.lastFlushAt = 0;
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Send data when component unmounts
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      sendEngagementData();
    };
  }, [handleScroll, sendEngagementData]);

  return {
    trackInteraction,
    trackCardViewed,
    trackCardClicked,
    trackFilterChange,
    trackModalOpened,
    trackTabViewed,
    trackSourceClicked,
    trackPodcastPlayed,
    trackShared,
    trackSaved,
    trackUnsaved,
    trackFullReportExpanded,
    trackSettingsOpened,
    trackCategoryBrowsed,
  };
}

export default useScreenEngagement;


